import React from "react";
import type { AssistantSegment } from "./assistantSegments.ts";
import { createBatchedActivityTranslator, postActivityTranslations } from "./batchedActivityTranslator.ts";
import {
  applyReasoningGate,
  createReasoningLanguageGuard,
  reasoningBlocksFromSegments,
  withoutReasoningText,
  type ReasoningLanguageGuard,
} from "./reasoningLanguageGuard.ts";

/** One queue per tab: every message's guard shares the concurrency limit. */
const translateActivityText = createBatchedActivityTranslator({ post: postActivityTranslations });

/**
 * Single display path for reasoning. AssistantTimeline is the only caller:
 * the status line and the Gedankengang both read the returned segments.
 *
 * Nothing is translated while `enabled` is false (a finished message with its
 * Gedankengang collapsed); its reasoning text is then returned empty.
 */
export function useGuardedReasoningSegments(
  segments: AssistantSegment[] | undefined,
  isSending: boolean,
  enabled: boolean,
): { segments: AssistantSegment[]; pending: boolean } {
  const guardRef = React.useRef<ReasoningLanguageGuard | null>(null);
  if (!guardRef.current) {
    guardRef.current = createReasoningLanguageGuard({
      translate: translateActivityText,
      timeoutMs: null,
    });
  }
  const blocks = reasoningBlocksFromSegments(segments, !isSending);
  const signature = blocks
    .map((block) => `${block.id}\u0000${block.done ? "1" : "0"}\u0000${block.text}`)
    .join("\u0001");
  const signatureRef = React.useRef("");
  const [, setRevision] = React.useState(0);
  const listenerRef = React.useRef(() => {});
  listenerRef.current = () => setRevision((value) => value + 1);
  if (!enabled) return { segments: withoutReasoningText(segments), pending: false };
  if (signatureRef.current !== signature) {
    signatureRef.current = signature;
    guardRef.current.sync(blocks, () => listenerRef.current());
  }
  const snapshot = guardRef.current.snapshot();
  const pending = [...snapshot.values()].some((state) => !state.settled);
  return { segments: applyReasoningGate(segments, snapshot), pending };
}
